import type { ChangeEvent } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  FormHelperText,
  Grid,
  Typography
} from '@mui/material';
import axios from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  editClientCompany,
  getAgent
} from '../../api/firebase/firestore/company';
import { registerCompanySchemaWhenClient } from '../../api/firebase/firestore/company/validation';
import { editUserToComplete } from '../../api/firebase/firestore/user';
import {
  black,
  blackText,
  cardBorder,
  gray,
  primaryButtonBackground
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  formWideMb,
  textFontSize,
  textMb,
  sizeBelowTenMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { clientSteps } from '../../constants/RegisterStep';
import { fiscalMonth, prefecture, sales } from '../../constants/Select';
import { screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';
import CustomLoading from '../uiParts/CustomLoading';

import CustomRedioButton from './CustomRadioButton';
import CustomSelectField from './CustomSelectField';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';
import ProductLists from './ProductLists';

import type { AxiosResponse } from 'axios';

export interface ClientRegisterCompanyType {
  logoId: string;
  introduction: string;
  foundationYear: number | undefined;
  listedFlg: boolean | undefined;
  zipcode: string;
  prefecture: number;
  city: string;
  address: string;
  sales: number;
  employees: number;
  fiscalMonth: number;
  agentFlg: boolean | undefined;
  agentLists: string | number | null;
  products: Array<{
    productNames: string | null;
    productUrls: string | null;
  }>;
  resources_0: string;
  resources_1: string;
  resources_2: string;
  resources_3: string;
  resources_4: string;
  sns_0: string;
  sns_1: string;
  sns_2: string;
  sns_3: string;
  sns_4: string;
  image: File;
}

interface List {
  value: number | string;
  option: string;
}

const styles = {
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  circle: {
    bgcolor: '#DBDBE0',
    borderRadius: '50%',
    height: '64px',
    marginBottom: '10px',
    width: '64px'
  },
  formWideMb,
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    }
  },
  imgCircle: {
    borderRadius: '50%',
    height: '64px',
    marginBottom: '10px',
    width: '64px'
  },
  label: {
    fontSize: '12px'
  },
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb,
  uploadButton: {
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    height: '36px',
    marginLeft: '11px',
    minWidth: '157px'
  },
  zipcodeButton: {
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    height: '30px',
    marginBottom: '25px',
    marginLeft: '16px',
    minWidth: '87px'
  }
};

interface Props {
  screenType: number;
}

const RegisterCompanyCard: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const steps = clientSteps();

  const { currentUser } = useContext(AuthContext);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [agentList, setAgentList] = useState<List[]>([
    { option: '', value: '' }
  ]);
  const navigate = useNavigate();
  const prefectureList = prefecture();
  const getPrefecture = (value: string): number => {
    const data = prefectureList.find(
      (element): boolean => element.option === value
    );
    return data ? data.value : 0;
  };

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm<ClientRegisterCompanyType>({
    defaultValues: {
      agentFlg: false,
      agentLists: 0,
      listedFlg: false,
      prefecture: 13,
      products: [
        {
          productNames: '',
          productUrls: ''
        },
        {
          productNames: '',
          productUrls: ''
        },
        {
          productNames: '',
          productUrls: ''
        },
        {
          productNames: '',
          productUrls: ''
        },
        {
          productNames: '',
          productUrls: ''
        }
      ],
      sales: 0,
      zipcode: ''
    },
    resolver: yupResolver(registerCompanySchemaWhenClient)
  });

  const [firstSubmit, setFirstSubmit] = useState(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('introduction') === undefined ||
        '' ||
        watch('foundationYear') === undefined ||
        '' ||
        watch('listedFlg') === undefined ||
        watch('zipcode') === undefined ||
        '' ||
        watch('prefecture') === undefined ||
        watch('city') === undefined ||
        '' ||
        watch('sales') === undefined ||
        '' ||
        watch('agentFlg') === undefined ||
        Boolean(errors.logoId) ||
        Boolean(errors.introduction) ||
        Boolean(errors.foundationYear) ||
        Boolean(errors.listedFlg) ||
        Boolean(errors.zipcode) ||
        Boolean(errors.prefecture) ||
        Boolean(errors.city) ||
        Boolean(errors.address) ||
        Boolean(errors.sales) ||
        Boolean(errors.employees) ||
        Boolean(errors.fiscalMonth) ||
        Boolean(errors.agentFlg) ||
        Boolean(errors.agentLists) ||
        Boolean(errors.products) ||
        Boolean(errors.resources_0) ||
        Boolean(errors.resources_1) ||
        Boolean(errors.resources_2) ||
        Boolean(errors.resources_3) ||
        Boolean(errors.resources_4) ||
        Boolean(errors.sns_0) ||
        Boolean(errors.sns_1) ||
        Boolean(errors.sns_2) ||
        Boolean(errors.sns_3) ||
        Boolean(errors.sns_4) ||
        Boolean(errors.image) ||
        Boolean(sizeError)
      );
    }
    return false;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [finishStatus, setfinishStatus] = useState(false);
  const [closeButton, setCloseButton] = useState(false);

  const browserBackAlert = (e: { preventDefault: () => void }): void => {
    if (!finishStatus) {
      e.preventDefault();
      setCloseButton(true);
      setOpen(true);
      setErrorText(t('form.browserBackAlert'));
      window.history.pushState(null, '', window.location.pathname);
      setfinishStatus(false);
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ): void => {
    if (reason === 'clickaway') return;
    setOpen(false);
    setCloseButton(false);
  };

  useEffect(() => {
    history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', browserBackAlert);
    return () => {
      window.removeEventListener('popstate', browserBackAlert);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const response = await getAgent(screenType.agent);
      setAgentList(response);
    })();
  }, []);

  useEffect(() => {
    setValue('agentLists', 0);
  }, [watch('agentFlg')]);

  const onSubmit = async (): Promise<void> => {
    try {
      if (currentUser?.uid) {
        setLoading(true);
        await editClientCompany(currentUser?.uid, watch, null, s3File, null);
        await editUserToComplete(currentUser?.uid);
        window.scrollTo(0, 0);
        props.screenType === screenType.client
          ? navigate('/client/auth/issue')
          : navigate('/agent/auth/issue');
      } else {
        throw new Error();
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  const [preview, setPreview] = useState('');
  const [s3File, setS3File] = useState<FileList | null>(null);
  const [sizeError, setSizeError] = useState<boolean>(false);

  const changeUploadFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setPreview(window.URL.createObjectURL(blob));
      setS3File(file);
    }
  };

  useEffect(() => {
    if (s3File && s3File.length > 0) {
      setSizeError(sizeBelowTenMb(s3File[0].size));
    } else {
      setSizeError(false);
    }
  }, [s3File]);

  interface Address {
    results: [
      {
        address1: string;
        address2: string;
        address3: string;
      }
    ];
  }

  const updateZipcodeSub = async (): Promise<void> => {
    try {
      const res: AxiosResponse<Address> = await axios.get(
        'https://zipcloud.ibsnet.co.jp/api/search',
        {
          params: {
            zipcode: watch('zipcode')
          }
        }
      );
      if (res.data.results) {
        const result = res.data.results[0];
        const prefectureNumber: number = getPrefecture(result.address1);
        const city: string = result.address2;
        const address: string = result.address3;
        setValue('prefecture', prefectureNumber);
        setValue('city', city);
        errors.city && clearErrors('city');
        setValue('address', address);
        errors.address && clearErrors('address');
      }
    } catch (e) {
      throwError(e);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ marginBottom: '24px', marginTop: '24px' }}
      >
        <Grid>
          <Card sx={[styles.cardSize, styles.cardBorder]}>
            <CardContent sx={styles.cardContentPadding}>
              <CustomStepper steps={steps} activeStep={1} />
              <Typography sx={[styles.titleMb, styles.title]} component="div">
                {t('registerClient.step2')}
              </Typography>
              <Typography component="div" sx={{ marginBottom: formWideMb }}>
                <Typography color={gray} sx={styles.label} component="span">
                  {t('form.logo')}
                </Typography>
                {preview ? (
                  <CardMedia image={preview} sx={styles.imgCircle} />
                ) : (
                  <Box sx={styles.circle} />
                )}
                <Typography component="div" sx={{ display: 'flex' }}>
                  <Typography
                    sx={[styles.label, { color: black, marginBottom: '4px' }]}
                  >
                    {t('form.logoDescription')}
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    sx={styles.uploadButton}
                  >
                    <Controller
                      control={control}
                      name="image"
                      render={({ field }) => (
                        <input
                          accept=".jpg, .jpeg, .png, .webp, .gif, .svg"
                          type="file"
                          hidden
                          onChange={(value) => {
                            field.onChange(value);
                            changeUploadFile(value);
                          }}
                        />
                      )}
                    />
                    <AddIcon sx={{ color: '#FFF' }} />
                    {t('form.logoUploadButton')}
                  </Button>
                </Typography>
                <FormHelperText
                  sx={styles.helper}
                  error={Boolean(errors.image)}
                >
                  {errors.image?.message}
                </FormHelperText>
                <FormHelperText sx={styles.helper} error={sizeError}>
                  {sizeError && t('validation.error.imageSize')}
                </FormHelperText>
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.introduction)}
                    helperText={errors.introduction?.message}
                    labelText={t('form.introduction')}
                    requiredText={t('form.required')}
                    isRequired
                    multiline
                    rows={7}
                    fieldType="textareaRow7"
                    placeholder={t('form.introductionPlaceholder')}
                  />
                )}
                control={control}
                name="introduction"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.foundationYear)}
                    helperText={errors.foundationYear?.message}
                    labelText={t('form.foundationYear')}
                    requiredText={t('form.required')}
                    isRequired
                    width="narrow"
                    type="number"
                    placeholder={t('form.yearEx')}
                  />
                )}
                control={control}
                name="foundationYear"
              />
              <Controller
                render={({ field }) => (
                  <CustomRedioButton
                    {...field}
                    error={Boolean(errors.listedFlg)}
                    helperText={errors.listedFlg?.message}
                    labelText={t('form.listedFlg')}
                    requiredText={t('form.required')}
                    isRequired
                    trueOption={t('form.listedTrue')}
                    falseOption={t('form.listedFalse')}
                  />
                )}
                control={control}
                name="listedFlg"
              />
              <Typography
                component="div"
                sx={{ alignItems: 'center', display: 'flex' }}
              >
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.zipcode)}
                      helperText={errors.zipcode?.message}
                      labelText={t('form.zipcode')}
                      requiredText={t('form.required')}
                      isRequired
                      width="narrow"
                      placeholder="0000000"
                      type="number"
                      underText={t('form.zipcodeGuideText')}
                    />
                  )}
                  control={control}
                  name="zipcode"
                />
                <Button
                  component="label"
                  variant="contained"
                  sx={styles.zipcodeButton}
                  onClick={() => {
                    void (async () => {
                      await updateZipcodeSub();
                    })();
                  }}
                >
                  {t('form.getAddress')}
                </Button>
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomSelectField
                    {...field}
                    error={Boolean(errors.prefecture)}
                    helperText={errors.prefecture?.message}
                    labelText={t('form.prefecture')}
                    requiredText={t('form.required')}
                    isRequired
                    width="narrow"
                    list={prefecture()}
                    blank
                  />
                )}
                control={control}
                name="prefecture"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.city)}
                    helperText={errors.city?.message}
                    labelText={t('form.city')}
                    requiredText={t('form.required')}
                    placeholder=" "
                    isRequired
                  />
                )}
                control={control}
                name="city"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.address)}
                    helperText={errors.address?.message}
                    labelText={t('form.address')}
                    isRequired={false}
                  />
                )}
                control={control}
                name="address"
              />
              <Controller
                render={({ field }) => (
                  <CustomSelectField
                    {...field}
                    error={Boolean(errors.sales)}
                    helperText={errors.sales?.message}
                    labelText={t('form.sales')}
                    placeholder={t('form.selectOther')}
                    requiredText={t('form.required')}
                    isRequired
                    defaultValue={0}
                    list={sales()}
                    blank
                  />
                )}
                control={control}
                name="sales"
              />
              <Typography
                component="div"
                sx={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.employees)}
                      helperText={errors.employees?.message}
                      labelText={t('form.employees')}
                      isRequired={false}
                      width="narrow"
                    />
                  )}
                  control={control}
                  name="employees"
                />
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      error={Boolean(errors.fiscalMonth)}
                      helperText={errors.fiscalMonth?.message}
                      labelText={t('form.fiscalMonth')}
                      isRequired={false}
                      defaultValue={0}
                      width="narrow"
                      list={fiscalMonth()}
                      type="number"
                      blank
                    />
                  )}
                  control={control}
                  name="fiscalMonth"
                />
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomRedioButton
                    {...field}
                    error={Boolean(errors.agentFlg)}
                    helperText={errors.agentFlg?.message}
                    labelText={t('form.agentFlg')}
                    requiredText={t('form.required')}
                    isRequired
                    trueOption={t('form.agentTrue')}
                    falseOption={t('form.agentFalse')}
                  />
                )}
                control={control}
                name="agentFlg"
              />
              {String(watch('agentFlg')) === 'true' ? (
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      placeholder={t('form.selectOther')}
                      isRequired={false}
                      defaultValue={0}
                      list={agentList}
                      blank
                    />
                  )}
                  control={control}
                  name="agentLists"
                />
              ) : (
                ''
              )}
              <ProductLists
                name="products"
                control={control}
                error={Boolean(errors.products)}
                helperText={errors.products?.message}
                helperTextsElm={errors.products}
              />
              <Typography component="div" sx={styles.formWideMb}>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_0)}
                      helperText={errors.resources_0?.message}
                      labelText={t('form.pressReleases')}
                      isRequired={false}
                      placeholder=" "
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="resources_0"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_1)}
                      helperText={errors.resources_1?.message}
                      isRequired={false}
                      placeholder=" "
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="resources_1"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_2)}
                      helperText={errors.resources_2?.message}
                      isRequired={false}
                      placeholder=" "
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="resources_2"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_3)}
                      helperText={errors.resources_3?.message}
                      isRequired={false}
                      placeholder=" "
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="resources_3"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_4)}
                      helperText={errors.resources_4?.message}
                      isRequired={false}
                      placeholder=" "
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="resources_4"
                />
              </Typography>
              <Typography component="div" sx={styles.formWideMb}>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_0)}
                      helperText={errors.sns_0?.message}
                      labelText={t('form.snsAccount')}
                      isRequired={false}
                      placeholder={t('form.facebook')}
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="sns_0"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_1)}
                      helperText={errors.sns_1?.message}
                      isRequired={false}
                      placeholder={t('form.Twitter')}
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="sns_1"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_2)}
                      helperText={errors.sns_2?.message}
                      isRequired={false}
                      placeholder={t('form.Instagram')}
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="sns_2"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_3)}
                      helperText={errors.sns_3?.message}
                      isRequired={false}
                      placeholder={t('form.TikTok')}
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="sns_3"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_4)}
                      helperText={errors.sns_4?.message}
                      isRequired={false}
                      placeholder={t('form.note')}
                      type="url"
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="sns_4"
                />
              </Typography>
              <CardActions sx={[styles.textMb, { padding: 0 }]}>
                <Grid container direction="column" alignItems="center">
                  <Button
                    disabled={buttonDisabledFlag()}
                    variant="contained"
                    fullWidth
                    sx={[
                      primaryButtonBackground,
                      {
                        '@media screen and (max-width: 960px)': {
                          height: '42px'
                        }
                      }
                    ]}
                    onClick={() => {
                      void (async () => {
                        setFirstSubmit(true);
                        await handleSubmit(async () => await onSubmit())();
                      })();
                    }}
                  >
                    <Typography
                      component="div"
                      color="white"
                      sx={{ fontSize: 15 }}
                    >
                      {t('registerClient.buttonToIssue')}
                    </Typography>
                  </Button>
                </Grid>
              </CardActions>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={closeButton ? handleClose : () => setOpen(false)}
        text={errorText}
        {...(closeButton && { closeButton: handleClose })}
      />
    </>
  );
};
export default RegisterCompanyCard;
